import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { DraftQuotation, SubmittedQuotation } from '~/api/quotations/types';

import { HttpBusinessConflictError } from '~/api/errors';
import { usePreviewQuotation, useUpdateQuotation } from '~/api/quotations';
import { useUser } from '~/api/user';
import { AlertDialog, Blocker, Button, Container, Dialog, Header, Separator } from '~/components';
import { Form } from '~/components/Form';
import { env } from '~/constants/env';
import { bypassBlocker } from '~/constants/router';
import { CompleteAccountDialog } from '~/features/account';
import { AutosaveStatus, CopyrightSection, FormLines, LinesTotal } from '~/features/documents';
import { useDialog } from '~/hooks/useDialog';
import { useIsFirstRender } from '~/hooks/useIsFirstRender';
import { routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';
import { isAccountCompleted } from '~/utils/user';

import type { CreateEditQuotationFormType } from './types';

import { mapQuotationFormDataToDraftPayload, mapQuotationFormDataToPayload } from '../quotations.utils';
import { QuotationPreviewDialog } from '../QuotationsOverview/components/QuotationPreviewDialog';
import { linesSectionStyle, sectionsStyle } from './CreateEditQuotationForm.css';
import { useAutoSave, useCalculationData, usePreviousEntryNumber, useQuotationDefaultValues } from './hooks';
import { QuotationFormCommandMenuItems } from './QuotationFormCommandMenuItems';
import { QuotationBasicSection } from './sections/QuotationBasicSection';
import { QuotationClientMessageSection } from './sections/QuotationClientMessageSection';

type Props =
  | {
      mode: 'create';
      quotation: DraftQuotation;
    }
  | {
      mode: 'edit';
      quotation: SubmittedQuotation;
    };

export const CreateEditQuotationForm = ({ mode, quotation }: Props) => {
  const { data: user } = useUser();

  const previewMutation = usePreviewQuotation(quotation.id);
  const updateQuotationMutation = useUpdateQuotation(quotation.id);

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'invoices', 'quotations']);

  const confirmationDialogHandle = Dialog.useHandle();
  const totalCoTooLowDialogHandle = AlertDialog.useHandle();
  const completeAccountDialog = useDialog<void, boolean>();
  const previousEntryNumber = usePreviousEntryNumber();

  const defaultValues = useQuotationDefaultValues(quotation);
  const form = useForm<CreateEditQuotationFormType>({ defaultValues });
  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
  } = form;

  const isFirstRender = useIsFirstRender();
  const isAutoSaving = useAutoSave(quotation.id, control, mode === 'create' && !updateQuotationMutation.isPending && !isFirstRender);
  const { calculationData, isFetching: isCalculationDataFetching } = useCalculationData(quotation, control);

  const onSubmit = handleSubmit(async () => {
    // Check account completion
    if (!isAccountCompleted(user) && !(await completeAccountDialog.open())) return;

    // CO below minimum commisison, meaning FC would be €0
    if (calculationData.fcExclVat === 0) return totalCoTooLowDialogHandle.open(null);

    // Ask confirmation
    confirmationDialogHandle.open(null);
  });

  const onConfirm = () => {
    updateQuotationMutation.mutate(mapQuotationFormDataToPayload(getValues()), {
      onSuccess: () => {
        toast.success({ description: t(`quotations:alerts.success${mode === 'create' ? 'Created' : 'Updated'}`) });
        navigate(routes.quotations, { state: bypassBlocker, replace: true });
      },
      onError: (error, { coEntryNumber }) => {
        toast.danger({
          description:
            error instanceof HttpBusinessConflictError && error.errorCode === 'QuotationEntryNumberMustBeUnique'
              ? t('quotations:alerts.errorDuplicateEntryNumber', { entryNumber: coEntryNumber })
              : t('common:error'),
        });
      },
    });
  };

  return (
    <>
      {mode === 'edit' && <Blocker isBlocked={isDirty} message={t('quotations:createEdit.blocker.message')} />}

      <Header.Root>
        <Header.Content backTo={routes.quotations} title={t(`quotations:createEdit.title.${mode}`)} />

        <Header.Actions>
          {mode === 'create' && <AutosaveStatus lastSavedAt={quotation.updatedAt} status={isAutoSaving ? 'saving' : 'saved'} />}

          <Header.Action
            emphasis="secondary"
            icon="Visibility"
            loading={previewMutation.isPending}
            onClick={() =>
              previewMutation.mutate(mapQuotationFormDataToDraftPayload(getValues()), {
                onError: () => toast.danger({ description: t('common:error') }),
              })
            }
          >
            {t('quotations:createEdit.preview')}
          </Header.Action>

          <Header.Action emphasis="primary" icon="Send" onClick={onSubmit}>
            {t('common:actions.send')}
          </Header.Action>
        </Header.Actions>
      </Header.Root>

      <Container>
        <Form autoComplete="off" onSubmit={onSubmit}>
          <div className={sectionsStyle}>
            {/* Basic info */}
            <QuotationBasicSection form={form} previousEntryNumber={mode === 'create' ? previousEntryNumber : null} />

            <Separator />

            {/* Royalties */}
            <CopyrightSection form={form} />

            <Separator />

            {/* Quotation lines */}
            <div className={linesSectionStyle}>
              <FormLines form={form} i18nNamespace="quotations" />
              <LinesTotal calculationData={calculationData} form={form} isLoading={isCalculationDataFetching} />
            </div>

            <Separator />

            {/* Message to client (mail + attachment) */}
            <QuotationClientMessageSection form={form} quotationId={quotation.id} />
          </div>
        </Form>
      </Container>

      {/* Confirmation dialog */}
      <Dialog.Root handle={confirmationDialogHandle}>
        <Dialog.Body>
          <Dialog.Header title={t('quotations:createEdit.confirmation.title')} />

          <p>{t('quotations:createEdit.confirmation.description')}</p>

          <Dialog.Actions>
            <Button icon="Send" loading={updateQuotationMutation.isPending} onClick={() => onConfirm()}>
              {t(`quotations:createEdit.confirmation.submit.${mode}`)}
            </Button>
          </Dialog.Actions>
        </Dialog.Body>
      </Dialog.Root>

      {/* Complete account dialog */}
      {completeAccountDialog.isOpen && (
        <CompleteAccountDialog onClose={() => completeAccountDialog.close(false)} onComplete={() => completeAccountDialog.close(true)} />
      )}

      {/* Preview dialog */}
      <QuotationPreviewDialog html={previewMutation.data} onClose={() => previewMutation.reset()} title={getValues('title') || null} />

      {/* CO too low dialog */}
      <AlertDialog.Root handle={totalCoTooLowDialogHandle}>
        <AlertDialog.Body status="danger">
          <AlertDialog.Title>{t('invoices:createEdit.coTooLowModal.title')}</AlertDialog.Title>

          <AlertDialog.Description>{t('invoices:createEdit.coTooLowModal.description')}</AlertDialog.Description>

          <AlertDialog.Actions>
            <AlertDialog.Close render={<Button>{t('invoices:createEdit.coTooLowModal.dismiss')}</Button>} />
          </AlertDialog.Actions>
        </AlertDialog.Body>
      </AlertDialog.Root>

      {(env.IS_LOCAL_DEV_ENV || env.IS_STAGING_ENV) && <QuotationFormCommandMenuItems form={form} />}
    </>
  );
};
